import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button,
  ButtonLink,
  Column,
  ContentBox,
  ContentPage,
  DescriptionList,
  Notice,
  Row,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import { schedulePeriod } from "@helpers/competencePeriod";
import { fixedAdjustmentCompetence, isCompetenceOpen } from "@helpers/fixedAdjustmentCompetence";
import { bankDataRoute } from "@helpers/bankData";
import { downloadTaxInvoice } from "@helpers/taxInvoiceFile";
import { getUploadBlockReason } from "@helpers";
import useService from "@hooks/useService";
import CompetenceValueBlock from "@components/CompetenceValueBlock";
import TaxInvoiceUploadModal from "@components/TaxInvoiceUploadModal";
import { Money, escapeHtml, formatCompetencia, usePageHeader } from "@components/ow";
import { remunerationSummary } from "../../Common/ContractDetail";
import {
  InvoiceFile,
  PaymentDetailSkeleton,
  Stack,
  competenceOf,
  contractValueItems,
  historyItems,
  paymentItems,
} from "../../Common/PaymentSchedules";

// Detalhe de um cronograma na visão do prestador (modelo Detalhe do DS OneWork, sem
// abas): a composição do valor, os blocos em duas colunas e o envio da nota. As ações
// do cronograma (editar, excluir, recalcular, marcar como pago) são da empresa.

const LIST_HREF = "/e/payment-schedules";

export default function EmployeePaymentScheduleDetails() {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const paymentScheduleService = useService("payment-schedules");
  const taxInvoicesService = useService("taxInvoices");
  const [loading, setLoading] = useState(true);
  const [schedule, setSchedule] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  // Última nota recusada da competência, quando o pagamento ficou sem nota (a recusa
  // desfaz o vínculo). `single`: o prestador tem só este pagamento na competência, então
  // a nota é deste pagamento; com mais de um, não dá para saber de qual.
  const [rejected, setRejected] = useState(null);

  // A API responde com "contract" (minúsculo); "Contract" fica como fallback
  // para respostas antigas/cacheadas.
  const contract = schedule?.contract || schedule?.Contract;
  const nf = schedule?.taxInvoice;
  const pending = schedule?.status === "PENDING";
  const competenceTitle = schedule ? formatCompetencia(competenceOf(schedule), true) : null;

  usePageHeader({
    title: competenceTitle ? `Pagamento · ${competenceTitle}` : "Pagamento",
    breadcrumb: [
      { page: "Pagamentos", route: LIST_HREF },
      {
        page: competenceTitle ? competenceTitle.charAt(0).toUpperCase() + competenceTitle.slice(1) : "Pagamento",
        route: `${LIST_HREF}/${params.id}`,
      },
    ],
  });

  useEffect(() => {
    fetchSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  async function fetchSchedule() {
    try {
      const result = await paymentScheduleService.get(params.id);
      if (result && result.data) {
        setSchedule(result.data);
        setLoading(false);
        fetchRejected(result.data);
        return;
      }
      throw new Error("Cronograma não encontrado");
    } catch (error) {
      console.error(error);
      const errorMessage = error?.response?.data?.msg || error?.message || "Erro ao carregar os detalhes do pagamento";
      // toastId: o StrictMode do dev busca duas vezes
      toast.error(errorMessage, { toastId: "employee-payment-schedule-load" });
      navigate(LIST_HREF);
    }
  }

  async function fetchRejected(current) {
    setRejected(null);
    if (current.status !== "PENDING" || current.taxInvoice) return;
    const month = Number(current.reference_month);
    const year = Number(current.reference_year);
    try {
      // Sem scope=company, as duas listas trazem só o que é do prestador
      const [invoices, schedules] = await Promise.all([
        taxInvoicesService.list(`status=REJECTED&month=${month}&year=${year}&order=id:desc&limit=1`),
        paymentScheduleService.list(`reference_month=${month}&reference_year=${year}&limit=2`),
      ]);
      const invoice = invoices?.data?.list?.[0];
      if (!invoice) return;
      setRejected({ invoice, single: (schedules?.data?.totalRegisters ?? 2) === 1 });
    } catch (error) {
      // Sem a busca, o bloco fica como antes: "Sem nota" e o envio
    }
  }

  if (loading || !schedule) return <PaymentDetailSkeleton />;

  const period = schedulePeriod(schedule);
  const competence = fixedAdjustmentCompetence(schedule, period);
  const open = isCompetenceOpen(schedule);

  // Mesmas regras da aba Pagamentos do contrato: dados bancários, valor, prazo e
  // competência em apuração. Os dados bancários vêm do /me guardado no login.
  const user = JSON.parse(localStorage.getItem("user_ow") || "{}");
  const noBank = !user.bankData?.pix_key && !user.bankData?.bank_code;
  // Sem nota, ou com a nota recusada, o prestador envia (de novo) enquanto o pagamento está pendente
  const canSend = pending && (!nf || nf.status === "REJECTED");
  const resend = (nf && nf.status === "REJECTED") || (!nf && rejected?.single);
  const blockReason = canSend ? getUploadBlockReason(schedule, contract, noBank) : null;

  const contractItems = contract ? [{ label: "Remuneração", value: remunerationSummary(contract) }, ...contractValueItems(contract)] : [];

  function sendBlock() {
    if (!canSend) return null;
    return (
      <Stack $gap="8px" $align="flex-start">
        <Button
          $text={resend ? "Reenviar nota" : "Enviar nota"}
          $variant="primary"
          $size="small"
          $noWidth
          $isDisabled={!!blockReason}
          $action={() => setShowUpload(true)}
        />
        {noBank ? (
          <Stack $gap="2px" $align="flex-start">
            <Text $text="Adicione seus dados bancários para enviar" $variant="caption_medium" $color="E400" />
            <ButtonLink $text="Adicionar dados bancários" $action={() => navigate(bankDataRoute(location))} $noPadding $noWidth />
          </Stack>
        ) : blockReason ? (
          <Text $text={blockReason} $variant="caption_medium" $color="E400" />
        ) : null}
      </Stack>
    );
  }

  function invoiceBlock() {
    if (!nf && rejected?.single) {
      return (
        <Stack $gap="12px">
          <DescriptionList
            $title="Nota fiscal"
            $items={[
              {
                label: "Arquivo",
                span: 2,
                value: <InvoiceFile invoice={rejected.invoice} onDownload={() => downloadTaxInvoice(taxInvoicesService, rejected.invoice)} />,
              },
              { label: "Valor da nota", value: rejected.invoice.value ? <Money value={rejected.invoice.value} /> : null },
            ]}
          />
          {rejected.invoice.rejection_reason ? (
            <Notice
              $variant="red"
              $title="Motivo da recusa"
              $text="%s"
              $replaces={[escapeHtml(rejected.invoice.rejection_reason)]}
            />
          ) : null}
          {sendBlock()}
        </Stack>
      );
    }
    if (!nf) {
      // Competência encerrada não recebe nota: não há o que enviar
      if (!pending) return null;
      return (
        <Stack $gap="12px">
          <DescriptionList $title="Nota fiscal" $columns={1} $items={[{ label: "Arquivo", value: "Sem nota" }]} />
          {rejected ? (
            <Notice
              $variant="gray"
              $text="Você tem uma nota recusada nesta competência."
              $actions={<ButtonLink $text="Ver notas fiscais" $action={() => navigate("/e/nfes")} $noPadding $noWidth />}
            />
          ) : null}
          {sendBlock()}
        </Stack>
      );
    }
    return (
      <Stack $gap="12px">
        <DescriptionList
          $title="Nota fiscal"
          $items={[
            {
              label: "Arquivo",
              span: 2,
              value: <InvoiceFile invoice={nf} onDownload={() => downloadTaxInvoice(taxInvoicesService, nf)} />,
            },
            { label: "Valor da nota", value: nf.value ? <Money value={nf.value} /> : null },
          ]}
        />
        {nf.status === "REJECTED" && nf.rejection_reason ? (
          <Notice $variant="red" $title="Motivo da recusa" $text="%s" $replaces={[escapeHtml(nf.rejection_reason)]} />
        ) : null}
        {sendBlock()}
      </Stack>
    );
  }

  return (
    <ContentPage>
      <ContentBox>
        <Stack $gap="24px">
          {open ? (
            <Notice
              $variant="orange"
              $title="Apuração parcial"
              $text="O valor desta competência ainda muda a cada aprovação das suas horas, até a nota ser enviada ou o prazo dela passar."
            />
          ) : null}
          {competence?.isFrozen ? (
            <Notice
              $variant="gray"
              $title="Encerrada para lançamento"
              $text="O prazo da nota passou e o valor não é mais recalculado. O saldo de horas é acertado na competência seguinte."
            />
          ) : null}

          <CompetenceValueBlock schedule={schedule} period={period} />

          <Row $gap={20} $alignItems="flex-start">
            <Column $columns={6}>
              <DescriptionList $title="Pagamento" $items={paymentItems(schedule, period)} />
            </Column>
            <Column $columns={6}>
              <Stack $gap="20px">
                {invoiceBlock()}
                {contract ? (
                  <Stack $gap="12px">
                    <DescriptionList $title="Contrato" $items={contractItems} />
                    {/* Num flex em coluna o link esticaria até a largura toda */}
                    <div>
                      <ButtonLink
                        $text="Ver contrato"
                        $action={() => navigate(`/e/contracts/${schedule.contract_id}`)}
                        $noPadding
                        $noWidth
                        $hasIcon
                      />
                    </div>
                  </Stack>
                ) : null}
                <DescriptionList $title="Histórico" $items={historyItems(schedule)} />
              </Stack>
            </Column>
          </Row>
        </Stack>
      </ContentBox>

      <TaxInvoiceUploadModal
        show={showUpload}
        onHide={() => setShowUpload(false)}
        schedule={schedule}
        nfeService={taxInvoicesService}
        onSuccess={() => {
          toast.success("Nota fiscal enviada com sucesso!");
          setShowUpload(false);
          fetchSchedule();
        }}
      />
    </ContentPage>
  );
}
